// Area model pictures (14 Oct 2026). An `area_model` is a rectangle split
// into rows and columns, with a number along the top of each column and
// down the left of each row. With `show` "products" each part carries its
// product; with "sides" the parts are empty. The total is never written or
// spoken: it is what the child is asked for.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type AreaModelSpec = {
  type: 'area_model';
  // The number down the left of each row, top to bottom.
  rows: number[];
  // The number along the top of each column, left to right.
  columns: number[];
  show: 'sides' | 'products';
};

const MAX_PARTS = 3;
// A bigger side number, or one with more decimal places, does not fit.
const MAX_SIDE = 99999;
const MAX_PLACES = 2;

function places(value: number): number {
  return (String(value).split('.')[1] ?? '').length;
}

// A side cannot be hidden: every row and every column carries its number.
function readSides(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  if (value.length < 1 || value.length > MAX_PARTS) return null;
  const sides: number[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'number' || !Number.isFinite(entry)) return null;
    if (entry <= 0 || entry > MAX_SIDE || places(entry) > MAX_PLACES)
      return null;
    sides.push(entry);
  }
  return sides;
}

export function readAreaModel(
  record: Record<string, unknown>,
): AreaModelSpec | null {
  const rows = readSides(record.rows);
  const columns = readSides(record.columns);
  const { show } = record;
  if (!rows || !columns) return null;
  if (show !== 'sides' && show !== 'products') return null;
  return { type: 'area_model', rows, columns, show };
}

// The product in one part, free of the stray digits that multiplying
// decimals leaves behind.
export function areaModelProduct(row: number, column: number): number {
  return Number((row * column).toFixed(2 * MAX_PLACES));
}

function spokenJoin(parts: string[]): string {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

function counted(count: number, thing: string): string {
  return `${String(count)} ${thing}${count === 1 ? '' : 's'}`;
}

// What a screen reader is told in place of the area model: how it is
// split and the number on each side, and the product in each part only
// when the picture shows it. Never the total.
export function describeAreaModel(model: AreaModelSpec): string {
  const { rows, columns } = model;
  const parts = [
    `An area model: a rectangle split into ${counted(rows.length, 'row')} and ${counted(columns.length, 'column')}.`,
    columns.length === 1
      ? `The number along the top is ${String(columns[0])}.`
      : `The numbers along the top are ${spokenJoin(columns.map(String))}.`,
    rows.length === 1
      ? `The number down the left side is ${String(rows[0])}.`
      : `The numbers down the left side are ${spokenJoin(rows.map(String))}.`,
  ];
  if (model.show === 'sides') {
    parts.push(
      rows.length * columns.length === 1
        ? 'The rectangle is empty.'
        : 'The parts are empty.',
    );
    return parts.join(' ');
  }
  const products = rows.map((row) =>
    spokenJoin(columns.map((column) => String(areaModelProduct(row, column)))),
  );
  if (rows.length * columns.length === 1)
    parts.push(`The number written inside it is ${products[0] ?? ''}.`);
  else if (rows.length === 1)
    parts.push(`The numbers written in the parts are ${products[0] ?? ''}.`);
  else
    parts.push(
      `The numbers written in the parts are, ${products
        .map((row, index) => `in row ${String(index + 1)}: ${row}`)
        .join('; ')}.`,
    );
  return parts.join(' ');
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type AreaModelPicture = {
  // Each column and row: where it starts, how big it is and its number.
  columns: { x: number; width: number; label: string }[];
  rows: { y: number; height: number; label: string }[];
  // The product in each part, row by row; empty unless they are shown.
  products: { x: number; y: number; text: string }[];
  left: number;
  top: number;
  width: number;
  height: number;
};

export const AREA_MODEL_LABEL_SIZE = 14;
const DIGIT_WIDTH = 8;
const TOP = 26;
const PAD = 6;

// A number as it is written on the picture: five digits or more are
// grouped in threes with a thin space, as the questions write them.
export function areaModelWritten(value: number): string {
  const [whole = '', after] = String(value).split('.');
  const grouped =
    whole.length > 4 ? whole.replace(/\B(?=(\d{3})+$)/g, ' ') : whole;
  return after === undefined ? grouped : `${grouped}.${after}`;
}

// A bigger number gets a bigger part, so 300 looks wider than 20, but not
// to scale: at true scale the part for the ones would be a sliver.
function sizes(numbers: number[], least: number, most: number): number[] {
  const largest = Math.max(...numbers);
  return numbers.map(
    (value) => least + (most - least) * Math.sqrt(value / largest),
  );
}

export function areaModelPicture(model: AreaModelSpec): AreaModelPicture {
  const rowLabels = model.rows.map(areaModelWritten);
  const left =
    Math.max(...rowLabels.map((label) => label.length)) * DIGIT_WIDTH + 14;
  const widths = sizes(model.columns, 62, 100);
  const heights = sizes(model.rows, 42, 64);
  const columns: AreaModelPicture['columns'] = [];
  let x = left;
  for (const [index, width] of widths.entries()) {
    columns.push({
      x,
      width,
      label: areaModelWritten(model.columns[index] ?? 0),
    });
    x += width;
  }
  const rows: AreaModelPicture['rows'] = [];
  let y = TOP;
  for (const [index, height] of heights.entries()) {
    rows.push({ y, height, label: rowLabels[index] ?? '' });
    y += height;
  }
  const products: AreaModelPicture['products'] = [];
  if (model.show === 'products')
    for (const [rowIndex, row] of rows.entries())
      for (const [columnIndex, column] of columns.entries())
        products.push({
          x: column.x + column.width / 2,
          y: row.y + row.height / 2,
          text: areaModelWritten(
            areaModelProduct(
              model.rows[rowIndex] ?? 0,
              model.columns[columnIndex] ?? 0,
            ),
          ),
        });
  return {
    columns,
    rows,
    products,
    left,
    top: TOP,
    width: x + PAD,
    height: y + PAD,
  };
}
